// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import tradeDealApi from './tradeDeal.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchTradeDealApi = createAsyncThunk(
    'heroSaga/tradeDeal/fetchTradeDealApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await tradeDealApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleTradeDealPublishedStateApi = createAsyncThunk(
    'heroSaga/tradeDeal/toggleTradeDealPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await tradeDealApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createTradeDealApi = createAsyncThunk(
    'heroSaga/tradeDeal/createTradeDealApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await tradeDealApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeTradeDealApi = createAsyncThunk(
    'heroSaga/tradeDeal/storeTradeDealApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const tradeDeal = await tradeDealApi.store(values);
            alertSwal('success', 'TradeDeal created successfully!');

            return tradeDeal;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showTradeDealApi = createAsyncThunk(
    'heroSaga/tradeDeal/showTradeDealApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await tradeDealApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateTradeDealApi = createAsyncThunk(
    'heroSaga/tradeDeal/updateTradeDealApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await tradeDealApi.update(id, values);
            alertSwal('success', 'TradeDeal updated successfully!');

            const { ranks } = state.heroSaga.tradeDealMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteTradeDealApi = createAsyncThunk('heroSaga/tradeDeal/deleteTradeDealApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await tradeDealApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const tradeDealSlice = createSlice({
    name: 'heroSaga/tradeDeal',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedTradeDeal(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTradeDealApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchTradeDealApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleTradeDealPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleTradeDealPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createTradeDealApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createTradeDealApi.rejected, () => {
                //
            });

        builder
            .addCase(storeTradeDealApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeTradeDealApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showTradeDealApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showTradeDealApi.rejected, () => {
                //
            });

        builder
            .addCase(updateTradeDealApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateTradeDealApi.rejected, () => {
                //
            });

        builder.addCase(deleteTradeDealApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedTradeDeal } = tradeDealSlice.actions;
